import React from "react";
import {
  RiShieldCheckLine,
  RiUser3Line,
  RiEBike2Line,
  RiTicketLine,
  RiPrinterLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiCheckLine,
} from "react-icons/ri";
import { toast } from "react-toastify";
import moment from "moment";

const WarrantyDetail = ({ warranty, onBack, onCreateRequest }) => {
  if (!warranty) return null;

  const warrantyId =
    warranty.invoiceNumber ||
    warranty._id ||
    warranty.id ||
    warranty.warrantyId ||
    "";
  const customerName =
    warranty.userid?.sfullname ||
    warranty.customerName ||
    warranty.name ||
    "-";
  const phone =
    warranty.mobileNumber ||
    warranty.userid?.imobilenumber ||
    warranty.phone ||
    warranty.mobile ||
    "-";
  const userMobile =
    warranty.userid?.imobilenumber ||
    warranty.mobileNumber ||
    warranty.phone ||
    warranty.mobile ||
    "-";
  const email =
    warranty.userid?.emailId ||
    warranty.appLogin?.email ||
    warranty.email ||
    "-";
  const billNo =
    warranty.invoiceNumber ||
    warranty.billNo ||
    warranty.billNumber ||
    "-";
  const purchaseDate =
    warranty.purchaseDate || warranty.billDate || warranty.createdDate;
  const startDate =
    warranty.warrantyStartDate || warranty.startDate || purchaseDate;
  const endDate =
    warranty.warrantyExpiryDate || warranty.endDate;
  const status =
    warranty.status === "YET_TO_ACTIVATE"
      ? warranty.status
      : (warranty.warrantyExpiryDate || warranty.endDate) &&
        moment(warranty.warrantyExpiryDate || warranty.endDate).isValid() &&
        moment(warranty.warrantyExpiryDate || warranty.endDate).isBefore(
          moment(),
          "day"
        )
      ? "EXPIRED"
      : warranty.status || "ACTIVE";

  const bikeBrand =
    warranty.bikeid?.brand?.brandName ||
    warranty.bicycle?.brand ||
    warranty.brand ||
    "-";
  const bikeModel =
    warranty.bikeid?.model ||
    warranty.bicycle?.model ||
    warranty.model ||
    "-";
  const bikeType =
    warranty.bikeid?.bikeType?.configValue ||
    warranty.bicycle?.bicycleType ||
    warranty.bicycleType ||
    "-";
  const gearType =
    warranty.bikeid?.gearSystem?.configValue ||
    warranty.bicycle?.gearType ||
    warranty.gearSystem ||
    "-";
  const frameNumber =
    warranty.bikeid?.frameNumber ||
    warranty.bicycle?.frameNumber ||
    warranty.frameNumber ||
    "-";

  let bikeImage = null;
  if (
    warranty.bikeid?.bikeImages?.[0]?.bikeImageStoragePath &&
    warranty.bikeid?.bikeImages?.[0]?.bikeImage
  ) {
    bikeImage = `https://jusride.s3.ap-south-1.amazonaws.com/${warranty.bikeid.bikeImages[0].bikeImageStoragePath}/${warranty.bikeid.bikeImages[0].bikeImage}`;
  } else if (warranty.bicycle?.image) {
    bikeImage = warranty.bicycle.image;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const m = moment(dateStr);
    return m.isValid() ? m.format("DD/MM/YYYY") : dateStr;
  };

  // Free services
  const totalFreeServices =
    warranty.totalService ?? warranty.freeServices?.total ?? 2;
  const takenFreeServices =
    warranty.serviceCount ??
    warranty.freeServices?.taken ??
    (warranty.serviceClaimedDate?.length || 0);
  const remainingFreeServices =
    warranty.servicesRemaining ??
    Math.max(0, totalFreeServices - takenFreeServices);
  const claimedDates = warranty.serviceClaimedDate || [];

  const remainingDays = endDate
    ? Math.max(0, moment(endDate).diff(moment(), "days"))
    : warranty.remainingDays || 0;

  const totalWarrantyDays =
    startDate && endDate
      ? Math.max(1, moment(endDate).diff(moment(startDate), "days"))
      : 730;
  const elapsedDays = Math.max(0, totalWarrantyDays - remainingDays);
  const progressPercent = Math.min(
    100,
    Math.max(5, Math.round((elapsedDays / totalWarrantyDays) * 100))
  );

  const handlePrint = () => {
    window.print();
  };

  const handleCreateRequestClick = (service) => {
    if (status !== "ACTIVE") {
      toast.warning(
        `Warranty is not active. Current status: ${getStatusLabel(status)}`,
        { position: "top-center" }
      );
      toast.clearWaitingQueue();
      return;
    }
    if (onCreateRequest) {
      onCreateRequest(warranty, service);
    } else {
      toast.info(
        `Initiating service request for ${customerName} (${service.label})`
      );
    }
  };

  const getStatusBadgeClass = (statusStr) => {
    const s = (statusStr || "").toLowerCase();
    if (s.includes("active") && !s.includes("yet")) return "active";
    if (s.includes("yet") || s.includes("activate")) return "yet-to-activate";
    if (s.includes("expire")) return "expired";
    return "active";
  };

  const getStatusLabel = (statusStr) => {
    if (!statusStr) return "Active";
    if (statusStr === "ACTIVE") return "Active";
    if (statusStr === "YET_TO_ACTIVATE") return "Yet to Activate";
    if (statusStr === "EXPIRED") return "Expired";
    return statusStr.replace(/_/g, " ");
  };

  // Build service steps list
  const servicesList =
    Array.isArray(warranty.freeServices?.services) &&
    warranty.freeServices.services.length > 0
      ? warranty.freeServices.services
      : Array.from({ length: totalFreeServices }).map((_, index) => {
          const isCompleted = index < takenFreeServices;
          const isEligible =
            index === takenFreeServices && status === "ACTIVE";
          const date = claimedDates[index] ? formatDate(claimedDates[index]) : null;
          return {
            number: index + 1,
            label: `Free Service ${index + 1}`,
            serviceTypeId: `free_service_${index + 1}`,
            completed: isCompleted,
            isNext: isEligible,
            date: date,
          };
        });

  return (
    <div className="warranty-detail-view">
      {/* Breadcrumb Navigation */}
      <div className="detail-breadcrumb">
        <span className="crumb-link" onClick={onBack}>
          <RiArrowLeftLine />
          Warranty Information
        </span>
        <span className="crumb-separator">&gt;</span>
        <span className="crumb-active">{billNo || warrantyId}</span>
      </div>

      {/* Header Row */}
      <div className="detail-header-row">
        <div className="title-badge-group">
          <h1>Warranty Detail</h1>
          <span className={`status-pill ${getStatusBadgeClass(status)}`}>
            {getStatusLabel(status)}
          </span>
        </div>

        <button className="print-btn" onClick={handlePrint}>
          <RiPrinterLine />
          Print
        </button>
      </div>

      {/* Grid: Top Left (Warranty Details) + Top Right (App Login & Bicycle Details) */}
      <div className="details-grid">
        {/* Card 1: Warranty Details */}
        <div className="detail-card">
          <div className="card-header">
            <div className="header-title">
              <RiShieldCheckLine />
              Warranty Details
            </div>
            <span className={`badge-tag ${getStatusBadgeClass(status)}`}>
              {getStatusLabel(status)}
            </span>
          </div>

          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">CUSTOMER</span>
              <span className="info-value">{customerName}</span>
            </div>

            <div className="info-item">
              <span className="info-label">PHONE</span>
              <span className="info-value">{phone}</span>
            </div>

            <div className="info-item">
              <span className="info-label">BILL NUMBER</span>
              <span className="info-value">{billNo}</span>
            </div>

            <div className="info-item">
              <span className="info-label">PURCHASE DATE</span>
              <span className="info-value">{formatDate(purchaseDate)}</span>
            </div>

            <div className="info-item">
              <span className="info-label">START DATE</span>
              <span className="info-value">{formatDate(startDate)}</span>
            </div>

            <div className="info-item">
              <span className="info-label">END DATE</span>
              <span className="info-value">{formatDate(endDate)}</span>
            </div>
          </div>

          {/* Timeline Box right below purchase date & dates */}
          <div className="warranty-timeline-box">
            <div className="timeline-dates-row">
              <div className="date-col">
                <span className="label">START DATE</span>
                <span className="date">{formatDate(startDate)}</span>
              </div>

              {remainingDays > 0 ? (
                <div className="remaining-badge">
                  <RiArrowRightLine />
                  <span>{remainingDays} Days Remaining</span>
                </div>
              ) : (
                <div className="remaining-badge" style={{ color: "#ef4444" }}>
                  <span>Warranty Expired</span>
                </div>
              )}

              <div className="date-col" style={{ alignItems: "flex-end" }}>
                <span className="label">END DATE</span>
                <span className="date">{formatDate(endDate)}</span>
              </div>
            </div>

            <div className="progress-track">
              <div
                className="progress-fill"
                style={{
                  width: `${progressPercent}%`,
                  backgroundColor: remainingDays > 0 ? "#10b981" : "#ef4444",
                }}
              />
            </div>
          </div>
        </div>

        {/* Right Stack: Customer Details & Bicycle Details */}
        <div className="right-side-stack">
          {/* Card 2: Customer Details */}
          <div className="app-login-card">
            <div className="card-header">
              <RiUser3Line />
              Customer Details
            </div>

            <div className="login-info-rows">
              <div className="login-row">
                <span className="label">NAME</span>
                <span className="value">{customerName}</span>
              </div>

              <div className="login-row">
                <span className="label">MOBILE</span>
                <span className="value">{userMobile}</span>
              </div>

              <div className="login-row">
                <span className="label">EMAIL</span>
                <span className="value">{email}</span>
              </div>
            </div>
          </div>

          {/* Card 3: Bicycle Details */}
          <div className="bicycle-details-card">
            <div className="card-header">
              <RiEBike2Line />
              Bicycle Details
            </div>

            {bikeImage && (
              <div className="bike-image-container">
                <img
                  src={bikeImage}
                  alt={`${bikeBrand} ${bikeModel}`}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}

            <div className="bike-specs-list">
              <div className="spec-row">
                <span className="label">BRAND</span>
                <span className="value">{bikeBrand}</span>
              </div>

              <div className="spec-row">
                <span className="label">MODEL</span>
                <span className="value">{bikeModel}</span>
              </div>

              <div className="spec-row">
                <span className="label">BICYCLE TYPE</span>
                <span className="value">{bikeType}</span>
              </div>

              <div className="spec-row">
                <span className="label">GEAR TYPE</span>
                <span className="value">{gearType}</span>
              </div>

              <div className="spec-row">
                <span className="label">FRAME NUMBER</span>
                <span className="value">{frameNumber}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card 4 (Bottom Full Width): Free-Service Tracker */}
      <div className="service-tracker-card">
        <div className="tracker-header">
          <div className="tracker-title-group">
            <div className="tracker-icon-box">
              <RiTicketLine />
            </div>
            <div className="text-group">
              <h3>Free-Service Tracker</h3>
              <p>Track your complimentary maintenance</p>
            </div>
          </div>

          <div className="stat-boxes">
            <div className="stat-box">
              <span className="stat-label">TOTAL</span>
              <span className="stat-count">{totalFreeServices}</span>
            </div>

            <div className="stat-box">
              <span className="stat-label">REMAINING</span>
              <span className="stat-count blue">{remainingFreeServices}</span>
            </div>

            <div className="stat-box">
              <span className="stat-label">TAKEN</span>
              <span className="stat-count green">{takenFreeServices}</span>
            </div>
          </div>
        </div>

        {/* Stepper Timeline */}
        <div className="stepper-timeline">
          {servicesList.map((svc, index) => {
            const isCompleted = svc.completed;
            const isEligible = svc.isNext || (!isCompleted && index === takenFreeServices && status === "ACTIVE");
            const isLast = index === servicesList.length - 1;
            const showCreateButton = !isCompleted && index === takenFreeServices && takenFreeServices < totalFreeServices;

            return (
              <React.Fragment key={svc.number || index}>
                <div className="step-item">
                  <div
                    className={`step-circle ${
                      isCompleted ? "completed" : isEligible ? "active" : "pending"
                    }`}
                  >
                    {isCompleted ? <RiCheckLine size={18} /> : svc.number}
                  </div>

                  <div className="step-info">
                    <span className="step-name">{svc.label}</span>
                    {isCompleted && svc.date && (
                      <span className="step-date">{svc.date}</span>
                    )}

                    {showCreateButton && (
                      <button
                        className="create-request-action"
                        onClick={() => handleCreateRequestClick(svc)}
                      >
                        Create Request
                      </button>
                    )}
                  </div>
                </div>

                {!isLast && (
                  <div
                    className={`step-connector ${
                      isCompleted ? "completed" : ""
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WarrantyDetail;
